/**
 * element-restyle.js — descendant restyling across a sibling multi-selection
 * (Polish 16C2).
 *
 * 16C1 made a row of cards selectable. This makes a change to something INSIDE
 * one of them apply to the equivalent thing inside the others: recolour the
 * SVG icon in the lead card and every selected card's icon follows, as ONE
 * history entry.
 *
 * The three ids the plan warned were being conflated stay separate here:
 *   - the clicked descendant eid   — what the user is editing
 *   - the repeat-root (unit) eid   — which card it lives in (16C1's identity)
 *   - the override target eid      — the counterpart inside each other card
 *
 * ## Why this is a resolver and not a writer
 *
 * Every panel already funnels through `applyElementOverrides`, so fanning out
 * THERE gives all ten-plus call sites 16C2 without touching one of them. This
 * module supplies the expansion through the injected seam
 * (`setElementRestyleResolver`) — `section-utils.js` cannot import it directly
 * without a cycle.
 *
 * ## Why the resolution is cached
 *
 * A glass scrub calls the writer at 60 Hz. Resolving per write would mean a
 * DOMParse per frame; the plan's "zero DOMParse at write time" is met by
 * resolving ONCE per (target, selection, section-html) and reusing it for every
 * subsequent write of the gesture. The cache is dropped whenever the selection
 * changes or the document does, so it can never outlive its own assumptions.
 *
 * ## The policy for units that do not match
 *
 * Validated across the corpus in `element-signature-corpus-check.js`: roughly
 * half of all sibling pairings are structurally heterogeneous, so this is the
 * common case, not an edge one.
 *
 *   A restyle applies to the units where the signature resolves and SKIPS the
 *   rest. It never falls back to an index — that is the "index-only DOM
 *   identity" this repo forbids, and it is exactly what would silently recolour
 *   the wrong element. The skipped count is announced once per resolution, so a
 *   partial application is visible rather than silent.
 */

import { eventBus } from '../core/events.js';
import { escapeForAttrSelector } from './editable-utils.js';
import { clearRepeatSelectorMarkers, withRepeatSelectorMarkers } from './element-insert.js';
import {
  elementSelection,
  getElementSelectionContext,
  getSelectedElementUnitEids
} from './element-selection.js';
import {
  computeRelativeTargetSignature,
  resolveRelativeTarget
} from './element-target-signature.js';
import { resolveSectionByInstance, setElementRestyleResolver } from './section-utils.js';

/** One entry, because a gesture only ever edits one target at a time. */
let _cache = null;
let _installed = false;

/**
 * Validity is DERIVED from the inputs, never from an invalidation event.
 *
 * The first cut invalidated on `state-changed` — which fires on EVERY write,
 * including inside a transaction (16B1) — so a 40-frame scrub re-parsed on
 * every frame and the plan's "zero DOMParse at write time" quietly did not
 * hold. `element-restyle-tests.js` counts parses for exactly that reason.
 *
 * Comparing the html the mapping was built from is cheaper and strictly more
 * correct: an override-only write leaves that string identical (same reference,
 * so the comparison short-circuits), and any structural edit replaces it.
 */
function cacheHit(instanceId, eid, html, selectionKey) {
  return (
    !!_cache &&
    _cache.instanceId === instanceId &&
    _cache.eid === eid &&
    _cache.selectionKey === selectionKey &&
    _cache.html === html
  );
}

/**
 * Expand one edited eid into every selected unit's counterpart.
 *
 * @returns {{eids: string[], skipped: number, relaxed: number}|null}
 *   null when there is nothing to fan out to (no multi-selection, another
 *   section, or a target that is not inside a selected unit).
 */
export function resolveRestyleTargets(instanceId, eid) {
  if (!instanceId || !eid) return null;
  if (!elementSelection.isMulti()) return null;

  const context = getElementSelectionContext();
  if (!context || context.instanceId !== instanceId || !context.itemSelector) return null;

  const html = resolveSectionByInstance(instanceId)?.section?.html;
  if (!html) return null;

  const selectionKey = getSelectedElementUnitEids().join(',');
  if (cacheHit(instanceId, eid, html, selectionKey)) return _cache.value;

  const value = _resolve(html, eid, context);
  _cache = { instanceId, eid, html, selectionKey, value };
  if (value && value.skipped > 0) {
    // Announced once per resolution, not per write — a scrub would otherwise
    // fire this every frame.
    eventBus.emit('element-restyle:partial', {
      instanceId,
      applied: value.eids.length,
      skipped: value.skipped
    });
  }
  return value;
}

function _resolve(html, eid, context) {
  if (typeof DOMParser === 'undefined') return null;
  let root;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    root = doc.body.firstElementChild;
  } catch {
    return null;
  }
  if (!root) return null;

  const hasMarkers = withRepeatSelectorMarkers(root, context.itemSelector);
  try {
    const target = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(eid)}"]`);
    if (!target) return null;
    const leadUnit = target.closest(context.itemSelector);
    if (!leadUnit) return null;

    const signature = computeRelativeTargetSignature(leadUnit, target);
    if (!signature) return null;

    const eids = [];
    let skipped = 0;
    let relaxed = 0;
    for (const unitEid of getSelectedElementUnitEids()) {
      const anchor = root.querySelector(`[data-pb-eid="${escapeForAttrSelector(unitEid)}"]`);
      const unit = anchor ? anchor.closest(context.itemSelector) : null;
      if (!unit) {
        skipped += 1;
        continue;
      }
      if (unit === leadUnit) {
        eids.push(eid);
        continue;
      }
      const resolved = resolveRelativeTarget(unit, signature);
      // No counterpart, or one with no eid to key an override on: SKIP. Never
      // stamp an eid here — a restyle must not mutate section HTML.
      const resolvedEid = resolved?.el?.getAttribute('data-pb-eid') || null;
      if (!resolvedEid) {
        skipped += 1;
        continue;
      }
      if (resolved.tier === 'relaxed') relaxed += 1;
      eids.push(resolvedEid);
    }

    if (eids.length <= 1) return null; // nothing gained; keep the single path
    return { eids, skipped, relaxed };
  } finally {
    if (hasMarkers) clearRepeatSelectorMarkers(root);
  }
}

/** Drop the memoized mapping. */
export function invalidateRestyleTargets() {
  _cache = null;
}

/**
 * Install the resolver into the shared element-override write path.
 * @returns {() => void} uninstall
 */
export function initElementRestyle() {
  if (_installed) return () => {};
  _installed = true;

  setElementRestyleResolver(
    (instanceId, eid) => resolveRestyleTargets(instanceId, eid)?.eids || null
  );

  // No invalidation listeners: the cache key already carries the selection and
  // the section html, so a stale entry cannot be read. A `state-changed`
  // listener here would fire on every write and defeat the memo entirely.
  return () => {
    setElementRestyleResolver(null);
    invalidateRestyleTargets();
    _installed = false;
  };
}

/** Test hook. */
export function _resetElementRestyleForTests() {
  setElementRestyleResolver(null);
  _cache = null;
  _installed = false;
}
